// Centralized, brand-voice display labels for skin-scan concerns/conditions.
//
// NAMING NOTE FOR DEVELOPERS:
// The KEYS in the maps below (skin_darkcircle, vascular, blemishes, ...) are the
// stable DEVELOPER-FACING identifiers used everywhere internally — API JSON
// keys, the premium_condition enum, ML scores and DB columns. They never change.
// End users only ever see the abstracted, gentle labels (brand voice: plain,
// cosmetic, "skin is read, not judged" — no medical/clinical wording). When the
// scan vocabulary needs to change for users, edit ONLY the label strings here.

/** Base-scan concern keys (the `skin_*` API fields) -> user-facing label. */
export const CONCERN_LABELS: Record<string, string> = {
  skin_acne: 'Texture flare-ups',
  skin_wrinkle: 'Expression lines',
  skin_darkcircle: 'Under-eye area',
  skin_skinredness: 'Warm tone saturation',
  skin_vascular: 'Deep tone saturation',
};

/** Premium-scan `condition_name` enum values -> user-facing label. */
const PREMIUM_CONDITION_LABELS: Record<string, string> = {
  blemishes: 'Texture flare-ups',
  fine_lines: 'Expression lines',
  dark_circles: 'Under-eye area',
  uneven_tone: 'Uneven tone',
  sun_spots: 'Sunlight marks',
  redness: 'Warm tone saturation',
  dryness: 'Moisture dip',
  oiliness: 'Excess shine',
  puffiness: 'Puffiness',
  pores: 'Pore visibility',
  firmness: 'Firmness',
  radiance: 'Radiance',
};

/**
 * Short backend concern names (skin_concerns.name: "darkcircle", "skinredness",
 * "vascular", ...) -> user-facing label. Used where the raw backend/model class
 * name would otherwise be shown (e.g. the model transparency card).
 */
const BACKEND_CONCERN_LABELS: Record<string, string> = {
  acne: 'Texture flare-ups',
  wrinkle: 'Expression lines',
  darkcircle: 'Under-eye area',
  redness: 'Warm tone saturation',
  skinredness: 'Warm tone saturation',
  vascular: 'Deep tone saturation',
};

/**
 * Basic-scan concern keys the app may show. Rendering is an allowlist, not a
 * blocklist: a key the app doesn't know (including a class the backend still
 * emits during a staged rollout) is dropped instead of shown under a raw name.
 * Kivo is cosmetic and non-medical — keep in sync with the mobile app's
 * `kSurfacedConcernKeys`.
 */
export const SURFACED_CONCERN_KEYS: ReadonlySet<string> = new Set([
  'acne',
  'wrinkle',
  'darkcircle',
  'redness',
  'skinredness',
  'vascular',
]);

/** Whether a backend concern name may be shown ("Dark circle", "skin_redness"…). */
export function isSurfacedConcern(name: string | null | undefined): boolean {
  return SURFACED_CONCERN_KEYS.has((name ?? '').toLowerCase().replace(/[^a-z]/g, ''));
}

/** Title-case fallback for any condition name not in the map. */
function titleCase(value: string): string {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

/**
 * Resolve a backend premium `condition_name` (e.g. "dark_circles") to its
 * abstracted, user-facing label. Falls back to a title-cased version so the UI
 * never breaks on an unmapped value.
 */
export function premiumConditionLabel(name: string): string {
  if (!name) return '';
  const key = name.trim().toLowerCase().replace(/\s+/g, '_');
  return PREMIUM_CONDITION_LABELS[key] ?? titleCase(name);
}

/** Resolve a base-scan `skin_*` key to its abstracted, user-facing label. */
export function concernLabel(key: string): string {
  return CONCERN_LABELS[key] ?? titleCase(key.replace(/^skin_/, ''));
}

/**
 * Resolve a short backend/model concern name (e.g. "skinredness", "dark circle")
 * to its abstracted, user-facing label. Tolerant of spaces/underscores/case.
 */
export function backendConcernLabel(name: string): string {
  if (!name) return '';
  const key = name.trim().toLowerCase().replace(/[\s_]+/g, '');
  return BACKEND_CONCERN_LABELS[key] ?? titleCase(name);
}
